import { Coffee, Gift, Heart, Star, X, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';

// PayPal Configuration
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'sb-your-sandbox-client-id';
const PAYPAL_ENVIRONMENT = 'sandbox';

// Donation amounts configuration
const DONATION_AMOUNTS = [
  { amount: 3, label: 'Buy me a coffee', icon: Coffee, description: 'Support development' },
  { amount: 10, label: 'Show appreciation', icon: Heart, description: 'Help improve Nalem Study Circle' },
  { amount: 25, label: 'Boost the project', icon: Star, description: 'Fund new features' },
  { amount: 50, label: 'Become a patron', icon: Gift, description: 'Major support' },
];

const MIN_CUSTOM_AMOUNT = 1;
const MAX_CUSTOM_AMOUNT = 500;

// PayPal Script Loader
const loadPayPalScript = () => {
  return new Promise((resolve, reject) => {
    if (window.paypal) {
      resolve(window.paypal);
      return;
    }

    const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
    if (existingScript) {
      existingScript.onload = () => resolve(window.paypal);
      existingScript.onerror = () => reject(new Error('PayPal script failed to load'));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD&intent=capture`;
    script.async = true;
    
    script.onload = () => {
      if (window.paypal) {
        resolve(window.paypal);
      } else {
        reject(new Error('PayPal failed to initialize'));
      }
    };
    
    script.onerror = () => reject(new Error('PayPal script failed to load'));
    document.head.appendChild(script);
  });
};

// Main Donation Banner Component
export const DonationBanner = ({ 
  darkMode, 
  showDonationBanner, 
  setShowDonationBanner,
  userPoints = 0,
  userName = 'Learning Enthusiast'
}) => {
  const [selectedAmount, setSelectedAmount] = useState(10);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [paypalError, setPaypalError] = useState(null);
  const [donationStats, setDonationStats] = useState({
    totalDonated: 0,
    donationCount: 0,
    lastDonation: null
  });

  // Load PayPal script on component mount
  useEffect(() => {
    if (showDonationBanner && !paypalLoaded && !paypalError) {
      setPaypalError(null);
      loadPayPalScript()
        .then(paypal => {
          console.log('PayPal loaded successfully');
          setPaypalLoaded(true);
        })
        .catch(error => {
          console.error('PayPal loading error:', error);
          setPaypalError(error.message);
        });
    }
  }, [showDonationBanner, paypalLoaded, paypalError]);

  // Load donation stats from localStorage
  useEffect(() => {
    try {
      const savedStats = localStorage.getItem('nalem_donation_stats');
      if (savedStats) {
        setDonationStats(JSON.parse(savedStats));
      }
    } catch (error) {
      console.error('Error loading donation stats:', error);
    }
  }, []);

  // Save donation stats to localStorage
  const saveDonationStats = (newStats) => {
    try {
      setDonationStats(newStats);
      localStorage.setItem('nalem_donation_stats', JSON.stringify(newStats));
    } catch (error) {
      console.error('Error saving donation stats:', error);
    }
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setIsCustom(false);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (value) => {
    const numValue = parseFloat(value);
    if (value === '' || (numValue >= MIN_CUSTOM_AMOUNT && numValue <= MAX_CUSTOM_AMOUNT)) {
      setCustomAmount(value);
      setIsCustom(true);
      if (numValue) {
        setSelectedAmount(numValue);
      }
    }
  };

  const getFinalAmount = () => {
    return isCustom && customAmount ? parseFloat(customAmount) : selectedAmount;
  };

  const handleDonationSuccess = (details) => {
    console.log('Donation successful:', details);
    const amount = getFinalAmount();
    const newStats = {
      totalDonated: donationStats.totalDonated + amount,
      donationCount: donationStats.donationCount + 1,
      lastDonation: new Date().toISOString()
    };
    
    saveDonationStats(newStats);
    setShowThankYou(true);
    setIsProcessing(false);
    
    setTimeout(() => {
      setShowThankYou(false);
      setShowDonationBanner(false);
    }, 5000);
  };

  const handleDonationError = (error) => {
    console.error('Donation error:', error);
    setIsProcessing(false);
    alert('Donation failed. Please try again or contact support if the issue persists.');
  };

  const createPayPalOrder = (data, actions) => {
    const amount = getFinalAmount();
    return actions.order.create({
      purchase_units: [{
        amount: {
          value: amount.toFixed(2),
          currency_code: 'USD'
        },
        description: `Nalem Study Circle Support - Thank you for your generosity!`
      }],
      application_context: {
        brand_name: 'Nalem Study Circle',
        locale: 'en-US',
        landing_page: 'BILLING',
        user_action: 'PAY_NOW'
      }
    });
  };

  const onPayPalApprove = (data, actions) => {
    setIsProcessing(true);
    return actions.order.capture()
      .then(details => {
        handleDonationSuccess(details);
      })
      .catch(error => {
        handleDonationError(error);
      });
  };

  const PayPalButton = () => {
    useEffect(() => {
      if (paypalLoaded && window.paypal && !paypalError) {
        const container = document.getElementById('paypal-button-container');
        if (container) {
          container.innerHTML = '';
          
          try {
            window.paypal.Buttons({
              createOrder: createPayPalOrder,
              onApprove: onPayPalApprove,
              onError: handleDonationError,
              onCancel: () => {
                console.log('Payment cancelled by user');
                setIsProcessing(false);
              },
              style: {
                layout: 'horizontal',
                color: 'blue',
                shape: 'rect',
                label: 'donate',
                height: 45,
                tagline: false
              }
            }).render('#paypal-button-container');
          } catch (error) {
            console.error('Error rendering PayPal button:', error);
            setPaypalError('Failed to render PayPal button');
          }
        }
      }
    }, [paypalLoaded, selectedAmount, customAmount, isCustom, paypalError]);

    return <div id="paypal-button-container" className="mt-4"></div>;
  };

  const AlternativeDonationButton = () => (
    <div className="mt-4 space-y-3">
      <div className={`p-4 rounded-xl ${darkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-100 border border-yellow-300'}`}>
        <p className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
          ⚠️ PayPal is temporarily unavailable. You can still support us through alternative methods:
        </p>
      </div>
      <button
        onClick={() => {
          window.open('mailto:pranayrishi.nalem@gmail.com?subject=Donation Support&body=I would like to make a donation of $' + getFinalAmount().toFixed(2) + ' to Nalem Study Circle. Please provide alternative payment instructions.');
        }}
        className="w-full bg-gradient-to-r from-green-500 to-blue-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
      >
        Contact for Alternative Payment
      </button>
    </div>
  );

  // Don't render if banner is not shown
  if (!showDonationBanner) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 sm:p-6 md:p-8">
      <div className={`w-full max-w-sm sm:max-w-md max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'}`}>
        {showThankYou ? (
          // Thank You Screen
          <div className="p-6 sm:p-8 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full bg-gradient-to-r from-green-500 to-blue-500 flex items-center justify-center">
              <Heart className="w-8 h-8 sm:w-10 sm:h-10 text-white animate-pulse" />
            </div>
            <h3 className={`text-xl sm:text-2xl font-bold mb-3 sm:mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Thank You, {userName}! 🎉
            </h3>
            <p className={`text-base sm:text-lg mb-3 sm:mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Your generous donation of <strong>${getFinalAmount().toFixed(2)}</strong> helps keep Nalem Study Circle free and amazing!
            </p>
            <div className={`p-3 sm:p-4 rounded-xl ${darkMode ? 'bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30' : 'bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-300'}`}>
              <p className={`text-sm ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>
                🚀 Your support helps us build better learning experiences for everyone!
              </p>
            </div>
            <div className="mt-4 sm:mt-6 grid grid-cols-3 gap-2 sm:gap-4 text-center">
              <div className={`p-2 sm:p-3 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className={`text-sm sm:text-lg font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                  ${donationStats.totalDonated.toFixed(2)}
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Total Support
                </div>
              </div>
              <div className={`p-2 sm:p-3 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className={`text-sm sm:text-lg font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                  {donationStats.donationCount}
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Donations
                </div>
              </div>
              <div className={`p-2 sm:p-3 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className={`text-sm sm:text-lg font-bold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                  {userPoints.toLocaleString()}
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Your Points
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Main Donation Screen
          <>
            {/* Header */}
            <div className={`p-4 sm:p-6 ${darkMode ? 'bg-gradient-to-r from-purple-900/50 to-pink-900/50' : 'bg-gradient-to-r from-purple-100 to-pink-100'}`}>
              <div className="flex justify-between items-start mb-3 sm:mb-4">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className={`text-lg sm:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      Support Nalem Study Circle
                    </h3>
                    <p className={`text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Help us keep learning free
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDonationBanner(false)}
                  className={`p-2 rounded-xl transition-all duration-300 flex-shrink-0 ${darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-200 text-gray-600'}`}
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
              
              <p className={`text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Nalem Study Circle is free for everyone. Your support helps us maintain servers, develop new features, and keep the platform ad-free.
              </p>
            </div>

            {/* Donation Amount Selection */}
            <div className="p-4 sm:p-6">
              <h4 className={`text-base sm:text-lg font-semibold mb-3 sm:mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Choose Your Support Level
              </h4>
              
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-6">
                {DONATION_AMOUNTS.map((option) => (
                  <button
                    key={option.amount}
                    onClick={() => handleAmountSelect(option.amount)}
                    className={`p-3 sm:p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                      selectedAmount === option.amount && !isCustom
                        ? 'border-purple-500 bg-purple-500/10'
                        : darkMode 
                        ? 'border-gray-700 hover:border-gray-600 bg-gray-800' 
                        : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-1 sm:mb-2">
                      <option.icon className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${selectedAmount === option.amount && !isCustom ? 'text-purple-500' : darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                      <span className={`font-bold text-sm sm:text-base ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        ${option.amount}
                      </span>
                    </div>
                    <div className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {option.label}
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {option.description}
                    </div>
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div className="mb-4 sm:mb-6">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Or enter a custom amount
                </label>
                <div className="relative">
                  <span className={`absolute left-3 top-3 text-sm sm:text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>$</span>
                  <input
                    type="number"
                    min={MIN_CUSTOM_AMOUNT}
                    max={MAX_CUSTOM_AMOUNT}
                    step="0.01"
                    value={customAmount}
                    onChange={(e) => handleCustomAmountChange(e.target.value)}
                    placeholder={`${MIN_CUSTOM_AMOUNT} - ${MAX_CUSTOM_AMOUNT}`}
                    className={`w-full pl-7 sm:pl-8 pr-4 py-3 rounded-xl border transition-all duration-300 text-sm sm:text-base ${
                      isCustom
                        ? 'border-purple-500'
                        : darkMode 
                        ? 'border-gray-700 bg-gray-800 text-white' 
                        : 'border-gray-300 bg-white text-gray-800'
                    } focus:outline-none focus:border-purple-500`}
                  />
                </div>
                {isCustom && customAmount && (
                  <p className={`text-sm mt-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    Custom amount: ${parseFloat(customAmount).toFixed(2)}
                  </p>
                )}
              </div>

              {/* Impact Message */}
              <div className={`p-3 sm:p-4 rounded-xl mb-4 sm:mb-6 ${darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-100 border border-blue-300'}`}>
                <div className="flex items-start space-x-3">
                  <Zap className={`w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  <div>
                    <h5 className={`font-semibold text-sm sm:text-base ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                      Your Impact
                    </h5>
                    <p className={`text-xs sm:text-sm ${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                      ${getFinalAmount().toFixed(2)} helps cover server costs for {Math.ceil(getFinalAmount() * 10)} students for a month!
                    </p>
                  </div>
                </div>
              </div>

              {/* PayPal Payment Button */}
              {paypalLoaded && !paypalError ? (
                <div>
                  <PayPalButton />
                  {isProcessing && (
                    <div className="text-center mt-4">
                      <div className={`inline-flex items-center space-x-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm">Processing donation...</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : paypalError ? (
                <AlternativeDonationButton />
              ) : (
                <div className="text-center py-6 sm:py-8">
                  <div className={`inline-flex items-center space-x-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm">Loading PayPal...</span>
                  </div>
                </div>
              )}

              {/* Security Note */}
              <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <p className={`text-xs text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  🔒 Secure payment powered by PayPal. We never store your payment information.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Top Donation Banner Component (Khan Academy style)
export const FloatingDonationButton = ({ 
  darkMode, 
  setShowDonationBanner,
  isDismissed = false,
  onDismiss
}) => {
  const [isVisible, setIsVisible] = useState(false);

  // Show banner after 30 seconds if not dismissed
  useEffect(() => {
    if (!isDismissed) {
      const showTimer = setTimeout(() => {
        setIsVisible(true);
      }, 30000); // 30 seconds

      return () => clearTimeout(showTimer);
    }
  }, [isDismissed]);

  // Don't render if dismissed or not visible yet
  if (isDismissed || !isVisible) return null;

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-50 transform transition-transform duration-500 ease-out ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
      style={{
        animation: isVisible ? 'slideDown 0.5s ease-out' : undefined
      }}
    >
      <style jsx>{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      
      <div className={`w-full shadow-lg border-b ${
        darkMode 
          ? 'bg-gradient-to-r from-purple-900 to-pink-900 border-purple-500/30' 
          : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'
      }`}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-6">
                  <div>
                    <h4 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      💖 Enjoying Nalem Study Circle?
                    </h4>
                    <p className={`text-xs ${darkMode ? 'text-purple-200' : 'text-purple-700'}`}>
                      Help us keep learning free and ad-free for everyone
                    </p>
                  </div>
                  <div className="hidden sm:flex items-center space-x-3">
                    <button
                      onClick={() => setShowDonationBanner(true)}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg font-medium text-sm hover:scale-105 transition-all duration-300 shadow-lg"
                    >
                      Donate Now
                    </button>
                    <button
                      onClick={() => {
                        onDismiss();
                        setIsVisible(false);
                      }}
                      className={`px-3 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${
                        darkMode 
                          ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                          : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Maybe Later
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Mobile buttons and close */}
            <div className="flex items-center space-x-2">
              <div className="sm:hidden flex space-x-2">
                <button
                  onClick={() => setShowDonationBanner(true)}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1.5 rounded-lg font-medium text-xs hover:scale-105 transition-all duration-300"
                >
                  Donate
                </button>
              </div>
              <button
                onClick={() => {
                  onDismiss();
                  setIsVisible(false);
                }}
                className={`p-1.5 rounded-lg transition-all duration-300 ${
                  darkMode 
                    ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-300' 
                    : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                }`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook for managing donation banner state - Shows on every visit
export const useDonationBanner = () => {
  const [showDonationBanner, setShowDonationBanner] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // No localStorage checking - banner will appear on every visit
  // isDismissed only affects current session

  // Handle dismissal - only for current session
  const handleDismiss = () => {
    setIsDismissed(true);
    // Note: Not saving to localStorage, so banner will show again on next visit
  };

  return {
    showDonationBanner,
    setShowDonationBanner,
    isDismissed,
    handleDismiss
  };
};